/**
 * Debug / Assume-User (impersonation) API client — NON-PRODUCTION ONLY.
 *
 * All calls go to /api/debug/impersonation. When the feature is disabled
 * (production, or env not opted in) these endpoints return 404 and callers
 * should treat the feature as unavailable.
 */

import apiClient from './client';
import type { User } from '../types/user';
import { decodeUser } from '../utils/decodeApiStrings';
import { decodeHtml } from '../lib/planningCycleLabels';

export interface ImpersonatorInfo {
  id: number;
  full_name: string;
  display_name?: string;
  email: string;
}

function decodeImpersonator(info: ImpersonatorInfo): ImpersonatorInfo {
  return {
    ...info,
    full_name: decodeHtml(info.full_name),
    display_name: info.display_name != null ? decodeHtml(info.display_name) : info.display_name,
    email: decodeHtml(info.email),
  };
}

function decodeSearchResult(row: ImpersonationSearchResult): ImpersonationSearchResult {
  return {
    ...row,
    full_name: decodeHtml(row.full_name),
    display_name: row.display_name != null ? decodeHtml(row.display_name) : row.display_name,
    email: decodeHtml(row.email),
    user_type_name: row.user_type_name != null ? decodeHtml(row.user_type_name) : row.user_type_name,
  };
}

function decodeTokenResponse(data: ImpersonationTokenResponse): ImpersonationTokenResponse {
  return {
    ...data,
    user: decodeUser(data.user),
    impersonator: data.impersonator ? decodeImpersonator(data.impersonator) : data.impersonator,
  };
}

export interface ImpersonationSearchResult {
  id: number;
  full_name: string;
  display_name?: string;
  email: string;
  is_admin: boolean;
  user_type_name: string | null;
}

export interface ImpersonationState {
  enabled: boolean;
  environment: string;
  is_impersonating: boolean;
  impersonator: ImpersonatorInfo | null;
  effective_user: {
    id: number;
    full_name: string;
    display_name?: string;
    email: string;
    is_admin: boolean;
  };
}

export interface ImpersonationTokenResponse {
  message: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: User;
  impersonator?: ImpersonatorInfo;
}

/** Probe availability + current impersonation status. 404 ⇒ feature disabled. */
export async function getImpersonationState(): Promise<ImpersonationState> {
  const { data } = await apiClient.get<ImpersonationState>('/debug/impersonation/state');
  return {
    ...data,
    impersonator: data.impersonator ? decodeImpersonator(data.impersonator) : data.impersonator,
    effective_user: {
      ...data.effective_user,
      full_name: decodeHtml(data.effective_user.full_name),
      display_name: data.effective_user.display_name != null
        ? decodeHtml(data.effective_user.display_name)
        : data.effective_user.display_name,
      email: decodeHtml(data.effective_user.email),
    },
  };
}

export async function searchImpersonationUsers(
  q: string,
  limit = 20,
): Promise<ImpersonationSearchResult[]> {
  const { data } = await apiClient.get<ImpersonationSearchResult[]>(
    '/debug/impersonation/search',
    { params: { q, limit } },
  );
  return (data ?? []).map(decodeSearchResult);
}

export async function startImpersonation(
  userId: number,
  reason?: string,
): Promise<ImpersonationTokenResponse> {
  const { data } = await apiClient.post<ImpersonationTokenResponse>(
    '/debug/impersonation/start',
    { user_id: userId, reason },
  );
  return decodeTokenResponse(data);
}

export async function stopImpersonation(): Promise<ImpersonationTokenResponse> {
  const { data } = await apiClient.post<ImpersonationTokenResponse>(
    '/debug/impersonation/stop',
  );
  return decodeTokenResponse(data);
}
